import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { filter, switchMap, tap, map } from 'rxjs/operators';
import { Organisation } from '../../organisation/organisation.interface';
import { User } from '../../user/user.interface';
import {
  organisationById,
  organisationList,
  selectCurrentOrganizationID,
} from '../../organisation/organisation.selector';
import {
  loadOrganisationById,
  setCurrentOrganisationID,
} from '../../organisation/organisation.actions';
import { myUserId, userById } from '../../user/user.selector';
import { loadHome } from '../../home/home.actions';
import {
  selectCurrentProjectIRI,
  selectCurrentProjectLabelFromEditor,
  selectCurrentProjectFromEditor,
  selectCurrentProjectID,
} from '../../element-editor/store/selector/editor.selector';
import {
  setResourceLabel,
  setResourcePatch,
} from '../../element-editor/store/editor.actions';
import { setDescriptorValue } from '../../element-editor/store/editor.crud.actions';
import { take } from 'rxjs/operators';
import { ProjectDescriptor } from '../../elements/resource/types/shape.type';

@Component({
  selector: 'nw-global-header',
  templateUrl: './global-header.component.html',
  styleUrls: ['./global-header.component.scss'],
})
export class GlobalHeaderComponent implements OnInit {
  orgs$ = this.store.select(organisationList);
  currentOrgId$ = this.store.select(selectCurrentOrganizationID);
  currentOrg$: Observable<Organisation | undefined> = this.currentOrgId$.pipe(
    filter((id): id is string => !!id),
    switchMap(id => this.store.select(organisationById(id))),
  );

  myUserId$ = this.store.select(myUserId);
  myUser$: Observable<User> = this.myUserId$.pipe(
    filter((id): id is string => !!id),
    switchMap(id => this.store.select(userById(id))),
  );

  // Project state (from editor state)
  projectLabel$ = this.store.select(selectCurrentProjectLabelFromEditor);
  currentProjectIRI$ = this.store.select(selectCurrentProjectIRI);
  private currentProject$ = this.store.select(selectCurrentProjectFromEditor);
  hasProject$ = this.store.select(selectCurrentProjectID).pipe(map(id => !!id));

  editingProject = false;
  editedProjectName = '';

  // Settings popup state
  showProjectSettings = false;
  projectFontFamily = 'Arial';
  projectPaletteRows: Array<{ name: string; color: string }> = [];

  showOrgDropdown = false;

  /** Whether we're on the /quest/:customToken route (minimal header mode) */
  isQuestRoute = false;

  @ViewChild('orgTrigger') orgTrigger?: ElementRef<HTMLElement>;
  @ViewChild('orgDropdown') orgDropdown?: ElementRef<HTMLElement>;
  @ViewChild('projectSettingsTrigger')
  projectSettingsTrigger?: ElementRef<HTMLElement>;
  @ViewChild('projectSettingsPopup')
  projectSettingsPopup?: ElementRef<HTMLElement>;

  constructor(
    private readonly store: Store,
    private readonly router: Router,
  ) {}

  currentProjectIRI: string;

  ngOnInit(): void {
    // Check initial route and subscribe to route changes
    this.checkIfQuestRoute(this.router.url);
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkIfQuestRoute(event.urlAfterRedirects || event.url);
      });

    // Only load home data if not on quest route
    if (!this.isQuestRoute) {
      this.store.dispatch(loadHome());
    }

    this.currentProjectIRI$.subscribe(iri => {
      this.currentProjectIRI = iri;
    });

    // Only set up org-related subscriptions if not on quest route
    if (!this.isQuestRoute) {
      combineLatest([this.orgs$, this.currentOrgId$])
        .pipe(
          tap(([orgs, currentId]) => {
            if (!currentId && orgs && orgs.length > 0) {
              this.store.dispatch(setCurrentOrganisationID({ ID: orgs[0].id }));
            }
          }),
        )
        .subscribe();

      this.currentOrgId$
        .pipe(filter((id): id is string => !!id))
        .subscribe(id => this.store.dispatch(loadOrganisationById({ id })));
    }
  }

  /**
   * Checks if the current URL is the quest route.
   */
  private checkIfQuestRoute(url: string): void {
    this.isQuestRoute = /^\/quest\//.test(url);
  }

  toggleOrgDropdown() {
    this.showOrgDropdown = !this.showOrgDropdown;
  }

  onSelectOrg(id: string) {
    if (!id) return;
    this.store.dispatch(setCurrentOrganisationID({ ID: id }));
    this.showOrgDropdown = false;
  }

  startEditProjectName(current: string) {
    this.editedProjectName = current || 'Untitled Project';
    this.editingProject = true;
  }

  cancelEditProjectName() {
    this.editingProject = false;
  }

  commitProjectName() {
    const label = (this.editedProjectName || '').trim() || 'Untitled Project';
    this.currentProjectIRI$.pipe(take(1)).subscribe(IRI => {
      if (!IRI) {
        this.editingProject = false;
        return;
      }
      this.store.dispatch(setResourceLabel({ IRI, label }));
      this.store.dispatch(
        setResourcePatch({ IRI, field: 'literals', key: 'label' }),
      );
      this.editingProject = false;
    });
  }

  // Settings popup
  toggleProjectSettings() {
    if (!this.showProjectSettings) {
      this.openProjectSettings();
    } else {
      this.showProjectSettings = false;
    }
  }

  private openProjectSettings() {
    this.currentProject$.pipe(take(1)).subscribe(project => {
      const descriptor = (project?.literals?.descriptor ||
        {}) as ProjectDescriptor;
      this.projectFontFamily = descriptor.fontFamily || 'Arial';
      const palette = descriptor.colorPalette || descriptor.colorPalette || {};
      this.projectPaletteRows = Object.entries(palette).map(
        ([name, color]) => ({
          name,
          color: String(color || ''),
        }),
      );
      if (this.projectPaletteRows.length === 0) {
        this.projectPaletteRows = [{ name: '', color: '' }];
      }
      this.showProjectSettings = true;
    });
  }

  addPaletteRow() {
    this.projectPaletteRows.push({ name: '', color: '' });
  }

  removePaletteRow(i: number) {
    this.projectPaletteRows.splice(i, 1);
    if (this.projectPaletteRows.length === 0) {
      this.projectPaletteRows.push({ name: '', color: '' });
    }
  }

  saveProjectSettings() {
    const fontFamily = (this.projectFontFamily || '').trim();
    const colorPalette = this.projectPaletteRows.reduce<Record<string, string>>(
      (acc, { name, color }) => {
        const n = (name || '').trim();
        const c = (color || '').trim();
        if (n && c) acc[n] = c;
        return acc;
      },
      {},
    );

    this.currentProjectIRI$.pipe(take(1)).subscribe(IRI => {
      if (!IRI) return;
      this.store.dispatch(
        setDescriptorValue({
          IRI,
          key: 'fontFamily',
          value: fontFamily,
          direct: true,
        }),
      );
      this.store.dispatch(
        setDescriptorValue({
          IRI,
          key: 'colorPalette',
          value: colorPalette,
          direct: true,
        }),
      );
      this.showProjectSettings = false;
    });
  }

  onProjectFontSelected(name: any) {
    this.projectFontFamily = String(name || '');
    console.log('onProjectFontSelected', name, this.projectFontFamily);
    this.store.dispatch(
      setDescriptorValue({
        key: 'fontFamily',
        value: this.projectFontFamily,
        IRI: this.currentProjectIRI,
        direct: true,
      }),
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showProjectSettings) {
      this.showProjectSettings = false;
    }

    if (this.showOrgDropdown) {
      this.showOrgDropdown = false;
    }
    return;

    const target = event.target as Node;

    // Close org dropdown when clicking outside
    if (this.showOrgDropdown) {
      const inTrigger = this.orgTrigger?.nativeElement.contains(target);
      const inDropdown = this.orgDropdown?.nativeElement?.contains(target);
      if (!inTrigger && !inDropdown) {
        this.showOrgDropdown = false;
      }
    }

    // Close project settings when clicking outside
    if (this.showProjectSettings) {
      const inTrigger =
        this.projectSettingsTrigger?.nativeElement.contains(target);
      const inPopup =
        this.projectSettingsPopup?.nativeElement?.contains(target);
      if (!inTrigger && !inPopup) {
        this.showProjectSettings = false;
      }
    }
  }
}
